import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatManageDate } from "@/lib/manageDateFormat";

export const TASK_TYPES = [
  { value: "general", label: "General" },
  { value: "procurement", label: "Procurement" },
  { value: "baking", label: "Baking" },
  { value: "frosting", label: "Frosting" },
  { value: "delivery", label: "Delivery" },
  { value: "shop", label: "Shop" },
  { value: "website", label: "Website" },
  { value: "marketing", label: "Marketing" },
  { value: "manager", label: "Manager" },
];

export const emptyTaskForm = () => ({
  text: "",
  task_type: "general",
  due_date: null,
  assigned_phone: "",
  order_id: "",
});

export function TaskFormFields({ f, setF, assignees, idPrefix = "task" }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>What needs to be done</Label>
        <Input
          value={f.text}
          onChange={(e) => setF({ ...f, text: e.target.value })}
          placeholder="e.g. Buy gold hearts for order, restock fondant…"
          className="mt-1 border-2 border-[#4A4A4A]"
          data-testid={`${idPrefix}-text`}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Type</Label>
          <Select value={f.task_type} onValueChange={(v) => setF({ ...f, task_type: v })}>
            <SelectTrigger className="mt-1 border-2 border-[#4A4A4A]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Assign to</Label>
          <Select
            value={f.assigned_phone || "__none__"}
            onValueChange={(v) => setF({ ...f, assigned_phone: v === "__none__" ? "" : v })}
          >
            <SelectTrigger className="mt-1 border-2 border-[#4A4A4A]">
              <SelectValue placeholder="Pick team member" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Unassigned</SelectItem>
              {(assignees || []).map((a) => (
                <SelectItem key={a.phone} value={a.phone}>
                  {a.name} ({a.group})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Due date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="mt-1 w-full border-2 border-[#4A4A4A] justify-start">
                {f.due_date ? formatManageDate(f.due_date.toISOString().slice(0, 10)) : "Pick date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={f.due_date} onSelect={(d) => setF({ ...f, due_date: d })} />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label>Order ID (optional)</Label>
          <Input
            value={f.order_id}
            onChange={(e) => setF({ ...f, order_id: e.target.value })}
            placeholder="Paste order UUID to link"
            className="mt-1 border-2 border-[#4A4A4A]"
          />
        </div>
      </div>
    </div>
  );
}

export function buildTaskPayload(form, assignees) {
  const text = form.text.trim();
  if (!text) return { error: "Enter a task description" };
  const assignee = (assignees || []).find((a) => a.phone === form.assigned_phone);
  let due_date;
  if (form.due_date) {
    const y = form.due_date.getFullYear();
    const m = String(form.due_date.getMonth() + 1).padStart(2, "0");
    const d = String(form.due_date.getDate()).padStart(2, "0");
    due_date = `${y}-${m}-${d}`;
  }
  return {
    payload: {
      text,
      task_type: form.task_type,
      due_date,
      order_id: form.order_id.trim() || undefined,
      assigned_to_name: assignee?.name || undefined,
      assigned_to_phone: assignee?.phone || undefined,
    },
  };
}
